import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { addGovernmental, editGovernmental } from "services/lealIntityService";
import { CrimeReference, Governmental, Investigation } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  isOpen: boolean;
  action: "Add" | "Edit";
  incident?: CrimeReference;
  investigation: Investigation;
  governmental?: Governmental;
  onClose: (refetch: boolean) => void;
};

const defaultGovernmental: Governmental = {
  uu_id: 0,
  governmental_type: "",
  office_name: "",
  department: "",
  address: "",
  investigation: null,
};

const VitimGovernmentalForm: FC<Props> = ({
  isOpen,
  action,
  incident,
  investigation,
  governmental,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const [formState, setFormState] = useState<Governmental>(
    governmental ?? defaultGovernmental,
  );

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (governmental && (investigation || incident)) {
      setFormState(governmental);
    }
  }, [governmental, investigation, incident]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState.office_name) return "Office Name is Required.";

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const submitAction = action === "Edit" ? editGovernmental : addGovernmental;

    submitAction({
      ...formState,
      investigation: investigation?.uu_id,
      crimereference: incident ? (incident?.uu_id as number) : null,
    } as Governmental)
      .then(() => {
        showTooltip(
          action === "Edit"
            ? "Successfully Updated!"
            : "Successfully Submitted!",
          "success",
        );
        setFormState(defaultGovernmental);
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">Add Governmental Victim</h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D] w-[400px]">
          publishing industries as a temporary filler.
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-4 mt-4">
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Governmental Type
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="governmental_type"
                value={formState?.governmental_type}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Office Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="office_name"
                value={formState?.office_name}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Department
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="department"
                value={formState?.department}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Address
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="address"
                value={formState?.address}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                                text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                                        ${
                                          loading
                                            ? "opacity-50 cursor-not-allowed"
                                            : ""
                                        }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : (
                action + " Governmental Victim"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VitimGovernmentalForm;
